/**
 * Hook for creating CRM objects with event-sourced architecture.
 *
 * Uses EventBuilder to construct creation events and optional associations.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';
import { isAsyncSingleObjectDeleteResponse } from '@/types/crm';

import { createCRMObjectClient } from '../core/CRMObjectClient';
import { createEventBuilder } from '../core/EventBuilder';
import { buildOptimisticCrmObject } from '../core/optimisticCrmObject';
import type { EntityTypeName } from '../core/types';
import { crmQueryKeys } from '../query/queries';
import {
  clearPendingCreations,
  getPendingCreationsSnapshot,
  getStoredObject,
  removeObject,
  upsertObject,
} from '../store/crmObjectStore';
import {
  applyOptimisticBatch,
  rollbackOptimisticBatch,
} from '../store/optimisticBatch';

interface CreateCrmObjectParams {
  properties: Record<string, unknown>;
  associations?: Array<{
    association_type_name: string;
    target_entity_id: string;
  }>;
}

/**
 * Hook to create a new CRM object.
 *
 * @param orgId - Organization ID
 * @param entityType - Entity type name (e.g., 'ahoy_contact', 'ahoy_deal')
 * @returns React Query mutation for creating CRM objects
 *
 * @example
 * const createContact = useCreateCrmObject(orgId, 'ahoy_contact');
 *
 * createContact.mutate({
 *   properties: { ahoy_first_name: 'John', ahoy_email: 'john@example.com' },
 *   associations: [{ association_type_name: 'ahoy_contact_to_company', target_entity_id: companyEntityId }],
 * });
 */
export function useCreateCrmObject(
  orgId: number | undefined,
  entityType: EntityTypeName
) {
  const queryClient = useQueryClient();
  const { user } = useAuthInfo();

  return useMutation({
    mutationFn: async ({
      properties,
      associations = [],
    }: CreateCrmObjectParams): Promise<CrmObject> => {
      if (!orgId || !user) {
        throw new Error('Org ID and user are required');
      }

      const client = createCRMObjectClient(orgId, entityType);
      const builder = createEventBuilder({
        sourceType: 'ahoy_user',
        sourceId: String(user.id),
      });

      const entityId = crypto.randomUUID();

      builder.objectCreated(entityId, entityType, properties);

      for (const assoc of associations) {
        builder.associationAdded(
          entityId,
          entityType,
          assoc.association_type_name,
          assoc.target_entity_id
        );
      }

      const events = builder.build();

      // Apply optimistically inside mutationFn (NOT onMutate) so this hook and the imperative
      // createCrmObject funnel share the same direct-primitive shape. Intentional asymmetry vs
      // useSubmitEventsOptimistic (which applies in onMutate) — do not "fix" it to match.
      const context = applyOptimisticBatch({ events }, orgId);

      try {
        const response = await client.submitEvents({ events });
        if (isAsyncSingleObjectDeleteResponse(response)) {
          // Object creation is never dispatched async — an async single-object-delete
          // response here is a contract violation. Roll back the optimistic batch and
          // surface an error rather than trying to interpret it as a create result.
          rollbackOptimisticBatch(context);
          throw new Error('Unexpected async response for object creation');
        }
        const resolvedEntityId = response.results?.[0]?.entity_id ?? entityId;

        if (resolvedEntityId !== entityId) {
          // Backend deduped/rewrote the create onto a different (hidden) entity — the client-id
          // optimistic entry is a phantom. Remove it, then fetch the authoritative object.
          rollbackOptimisticBatch(context);

          // The seq-guard in rollbackOptimisticBatch declines to delete an entry whose
          // seq advanced past our optimistic write (it looks like legitimate newer
          // data — e.g. a concurrent edit landed on the phantom before rollback ran).
          // But the server's own response is authoritative proof that `entityId` is a
          // phantom that will never be confirmed, so that guard doesn't apply here —
          // force-remove it directly rather than leaving it to coexist with the
          // canonical entity below.
          const phantomStillPresent =
            getStoredObject(entityId) !== undefined ||
            getPendingCreationsSnapshot().has(entityId);
          if (phantomStillPresent) {
            clearPendingCreations([entityId]);
            removeObject(entityId);
            logger.warn(
              'Optimistic phantom entity survived rollback seq-guard — force-removed after dedup rewrite',
              {
                component: 'crm_events',
                entityId,
                resolvedEntityId,
                orgId,
                entityType,
              }
            );
          }

          logger.warn(
            'Entity ID rewritten by backend (dedup) — removed optimistic entry',
            {
              component: 'crm_events',
              originalEntityId: entityId,
              resolvedEntityId,
              orgId,
              entityType,
            }
          );

          try {
            return await client.get(resolvedEntityId);
          } catch (getErr) {
            logger.error(
              'Failed to fetch entity after backend rewrote create (dedup)',
              getErr instanceof Error ? getErr : new Error(String(getErr)),
              {
                component: 'crm_events',
                resolvedEntityId,
                orgId,
                entityType,
              }
            );
            throw getErr;
          }
        }

        // The WS echo of our own submit carries the SAME seq as the optimistic
        // write, so the store's seq guard skips it — server-normalized/generated
        // fields would otherwise stay stale. Hydrate from the server directly and
        // upsert it; upsertObject makes the authoritative fields win while still
        // merging any concurrent WS edit that advanced the store past this fetch.
        try {
          const authoritative = await client.get(entityId);
          upsertObject(authoritative);
          return getStoredObject(entityId)?.data ?? authoritative;
        } catch (getErr) {
          // The create itself already succeeded — a re-fetch failure is not a
          // reason to roll back. Fall back to whatever the store/optimistic
          // build already has.
          logger.warn(
            'Failed to hydrate authoritative entity after create — falling back to optimistic data',
            {
              component: 'crm_events',
              entityId,
              orgId,
              entityType,
              error: getErr instanceof Error ? getErr.message : String(getErr),
            }
          );
          return (
            getStoredObject(entityId)?.data ??
            // Mirrors the store's optimistic entry so the common (non-dedup)
            // path can resolve without a `client.get()` round-trip.
            buildOptimisticCrmObject({
              entityId,
              entityType,
              orgId,
              properties,
              associations,
            })
          );
        }
      } catch (err) {
        rollbackOptimisticBatch(context);
        // Log from here (not a hook-level onError) so the entityId is the one
        // this specific mutation generated, not a hook-shared variable that a
        // later concurrent create may have already overwritten.
        logger.error(
          'Failed to create CRM object',
          err instanceof Error ? err : new Error(String(err)),
          {
            component: 'crm_events',
            orgId,
            entityType,
            entityId,
          }
        );
        throw err;
      }
    },

    onSuccess: () => {
      if (orgId) {
        queryClient.invalidateQueries({
          queryKey: crmQueryKeys.objects(orgId, entityType),
        });
      }
    },
  });
}
